<template>
  <a-modal
      :open="open"
      class="modal-no-padding add-admins-modal"
      :footer="null"
      @cancel="closeForm"
  >
    <div class="modal-no-padding__header">
      <h2>Добавление администраторов</h2>
    </div>

    <a-divider style="margin: 16px 0" />

    <div class="modal-no-padding__body">
      <a-form layout="vertical" :model="form">
        <a-form-item label="Действующие сотрудники">
          <a-select
              v-model:value="form.users"
              show-search
              allow-clear
              mode="multiple"
              placeholder="Не выбрано"
              :not-found-content="usersLoading ? 'Поиск…' : 'Нет данных'"
              :loading="usersLoading"
              :filter-option="false"
              :options="userOptions"
              :show-arrow="true"
              class="add-admins-modal__select"
              @search="debouncedSearch"
              @clear="clearUsers"
          />
        </a-form-item>
      </a-form>
    </div>

    <a-divider style="margin: 16px 0" />

    <div class="modal-no-padding__footer">
      <div class="add-admins-modal__actions">
        <a-button class="secondary-button" @click="closeForm">
          Отменить
        </a-button>

        <a-button
            class="primary-button"
            :loading="modalActionLoading"
            @click="submitForm"
        >
          Подтвердить
        </a-button>
      </div>
    </div>
  </a-modal>
</template>

<script>
import { computed, onBeforeMount, reactive, ref, watch } from 'vue'
import { message } from 'ant-design-vue'
import { useStore } from 'vuex'
import debounce from '@/mixins/debounce'

export default {
  name: 'AddHRAdmin',

  props: {
    open: {
      type: Boolean,
      default: false,
    },
  },

  emits: ['submitted', 'close'],

  setup(props, context) {
    const store = useStore()

    const modalActionLoading = ref(false)
    const usersLoading = ref(false)
    const userOptions = ref([])
    const currentSearchRequest = ref(0)

    const foundUsers = computed(() => store.state.users.foundUsers || [])

    const form = reactive({
      users: [],
    })

    const normalizeUserOption = (user) => ({
      value: user.id,
      label: user.email ? `${user.name} (${user.email})` : user.name,
    })

    const clearUsers = () => {
      form.users = []
    }

    const searchForUsers = async (query) => {
      if (!query || query.length < 3) return

      const requestNumber = ++currentSearchRequest.value

      usersLoading.value = true
      userOptions.value = []

      try {
        await store.dispatch('users/searchByName', query);

        if (requestNumber !== currentSearchRequest.value) return

        const uniqueUsers = foundUsers.value
            .filter((user) => {
              return !userOptions.value.some((option) => option.value === user.id)
            })
            .map(normalizeUserOption)

        userOptions.value.push(...uniqueUsers)
      } finally {
        if (requestNumber === currentSearchRequest.value) {
          usersLoading.value = false
        }
      }
    }

    const debouncedSearch = debounce(searchForUsers, 350)

    const submitForm = async () => {
      modalActionLoading.value = true

      try {
        await store.dispatch('hrAdmin/setUsersAsAdmins', {
          users: form.users,
        })

        message.success('Администраторы добавлены')
        store.dispatch('users/clearFoundUsers')

        context.emit('submitted', {
          users: form.users,
        })
      } finally {
        modalActionLoading.value = false
      }
    }

    const closeForm = () => {
      store.dispatch('users/clearFoundUsers')
      context.emit('close')
    }

    return {
      form,
      modalActionLoading,
      usersLoading,
      userOptions,
      debouncedSearch,
      clearUsers,
      submitForm,
      closeForm,
    }
  },
}
</script>

<style scoped lang="scss">
.add-admins-modal {
  h2 {
    margin: 0;
    font-weight: 600;
    font-size: 20px;
    line-height: 120%;
  }
}

:deep(.ant-form-item-label) {
  padding: 0 0 4px !important;
}

.add-admins-modal__select {
  width: 100%;
}

.add-admins-modal__actions {
  display: flex;
  justify-content: flex-end;
  column-gap: 10px;
}
</style>